<!-- eslint-disable id-length -->

<script setup lang="ts">
import AnimationRotating from './AnimationRotating.vue'
import { UIcon, UKbd } from '#components'

const props = defineProps({
  canSubmit: {
    default: false,
    type: Boolean,
  },
  cleanupTooltip: {
    default: 'Cleanup',
    type: String,
  },
  completable: {
    default: true,
    type: Boolean,
  },
  completions: {
    default: () => [],
    type: Array as PropType<InputCompletion[]>,
  },
  completionsDirection: {
    default: 'bottom',
    type: String as PropType<'bottom' | 'top'>,
  },
  disabled: {
    default: false,
    type: Boolean,
  },
  forceButton: {
    default: false,
    type: Boolean,
  },
  forceCleanup: {
    default: false,
    type: Boolean,
  },
  forceSubmit: {
    default: false,
    type: Boolean,
  },
  inputStyle: {
    default: undefined,
    type: Object,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  placeholders: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  showCleanup: {
    default: true,
    type: Boolean,
  },
  showContent: {
    default: false,
    type: Boolean,
  },
  stealFocus: {
    default: true,
    type: Boolean,
  },
  submitTooltip: {
    default: 'Submit',
    type: String,
  },
})

const emit = defineEmits<{
  blur: []
  change: []
  cleanup: []
  focus: []
  reset: []
  submit: [value: string]
}>()

const { t } = useI18n()

const cleanupButtonRef = templateRef<HTMLButtonElement>('cleanupButtonRef')
const inputContainerRef = templateRef<HTMLDivElement>('inputContainerRef')
const inputRef = templateRef<HTMLInputElement>('inputRef')
const submitButtonRef = templateRef<HTMLButtonElement>('submitButtonRef')

const model = defineModel<string>({ default: '' })
const selectedCompletion = defineModel<InputCompletion | undefined>('completion', { default: undefined })
const completionsForce = defineModel<boolean | undefined>('completionsForce', { default: undefined })
const focused = defineModel<boolean>('focused', { default: false })
const selectedIndex = defineModel<number>('selectedIndex', { default: -1 })

// Normal refs
const animating = ref<boolean>(false)
const animationFrame = ref<null | number>(null)
const inputWidth = ref<number>(0)

// First, let's add a computed for the displayed completions order
const displayedCompletions = computed(() => {
  return props.completionsDirection === 'top' ? [...props.completions].reverse() : props.completions
})

function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible' && animationFrame.value) {
    cancelAnimationFrame(animationFrame.value)
    animationFrame.value = null
  }
  else if (document.visibilityState === 'visible') {
    animationFrame.value = requestAnimationFrame(() => {
      // AnimationRotating handles this now
    })
  }
}

function updateInputWidth(): void {
  if (inputContainerRef.value) {
    inputWidth.value = inputContainerRef.value.clientWidth
  }
}

function focusInput(): void {
  inputRef?.value?.focus?.()
}

// Add a ref to track if we're using keyboard navigation
const isKeyboardNavigation = ref(false)

async function handleKeyDown(e: KeyboardEvent): Promise<void> {
  const isTopDirection = props.completionsDirection === 'top'
  const isArrowDown = e.key === 'ArrowDown'
  const isArrowUp = e.key === 'ArrowUp'

  // Reset
  if (e.key === 'Escape') {
    // First reset completions if it is opened
    if (props.completions.length) {
      completionsForce.value = undefined
      selectedIndex.value = -1

      return
    }

    emit('reset')
  }

  // Reset if Ctrl/Cmd+Backspace or Ctrl/Cmd+Delete is pressed
  if ((e.key === 'Backspace' || e.key === 'Delete') && (e.ctrlKey || e.metaKey)) {
    emit('reset')

    return
  }

  // Skip if no completions and no arrow keys
  if (isArrowUp || isArrowDown) {
    // Force open completions if no completions
    if (!props.completions.length) {
      completionsForce.value = true

      return
    }
  }

  // Reset the input if we're deleting the last character
  if (e.key === 'Backspace' && model.value === '') {
    e.preventDefault()

    selectedIndex.value = -1

    emit('reset')

    return
  }

  // If we're showing autocomplete, handle keyboard navigation inside it
  if (props.completions.length) {
    if (isArrowDown || isArrowUp) {
      e.preventDefault()

      isKeyboardNavigation.value = true

      // Invert the arrow logic for top direction
      const shouldIncrement = isTopDirection ? isArrowUp : isArrowDown

      if (shouldIncrement) {
        selectedIndex.value = (selectedIndex.value + 1) % props.completions.length
      }
      else {
        selectedIndex.value = selectedIndex.value - 1 < 0
          ? props.completions.length - 1
          : selectedIndex.value - 1
      }

      // If we're at the top, force completions to close
      if (selectedIndex.value < 0) {
        completionsForce.value = undefined
        selectedIndex.value = -1

        return
      }

      const completion = props.completions[selectedIndex.value]

      // If the completion has an onKeyboardNavigation function, call it when it is active in the dropdown
      if (completion?.onKeyboardNavigation) {
        await completion.onKeyboardNavigation(completion)
      }

      return
    }

    // Select completion on enter
    if (e.key === 'Enter') {
      e.preventDefault()

      await selectCompletion(props.completions[selectedIndex.value])

      return
    }
  }

  if (e.key === 'Enter' && props.canSubmit) {
    e.preventDefault()

    handleSubmit()
  }
}

async function selectCompletion(completion: InputCompletion): Promise<void> {
  if (completion?.onSelect) {
    await completion.onSelect(completion)
  }

  completionsForce.value = undefined
  selectedIndex.value = -1
}

function handleSubmit(): void {
  emit('submit', model.value)
}

function handleCleanup(): void {
  emit('cleanup')
}

function handleFocus(): void {
  focused.value = true
  emit('focus')
}

function handleBlur(): void {
  focused.value = false
  emit('blur')
}

watch(
  () => props.completions.length,
  (newVal: number) => {
    // Reset to no index when completions are empty
    if (newVal === 0) {
      selectedIndex.value = -1
      completionsForce.value = undefined
    }
  },
)

watch(
  model,
  (newVal: string, oldVal: string) => {
    if (newVal.length === 0 && oldVal.length > 0) {
      selectedIndex.value = -1
      completionsForce.value = undefined
    }
  },
)

watch(
  completionsForce,
  (newVal: boolean | undefined) => {
    if (newVal) {
      selectedIndex.value = 0
    }
  },
)

// Initialize spring values based on initial showContent value
onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Add resize listener to update input width
  window.addEventListener('resize', updateInputWidth)

  if (props.stealFocus) {
    // Focus input when mounted
    inputRef?.value?.focus?.()
  }
})

onBeforeUnmount(() => {
  if (animationFrame.value) {
    cancelAnimationFrame(animationFrame.value)
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('resize', updateInputWidth)
})
</script>

<template>
  <form
    class="transition-base shadow-texture relative z-40 h-14 w-full overflow-visible rounded-full border border-gray-200 !bg-white shadow-inner dark:border-gray-800 dark:!bg-gray-950"
    :class="[
      model && 'bg-gray-50',
      {
        'cursor-text': !showContent,
      },
    ]"
    @click="focusInput"
    @submit.prevent="handleSubmit"
  >
    <div class="size-full w-full overflow-hidden px-6">
      <Transition
        enter-active-class="transition-base"
        enter-from-class="opacity-0 translate-y-12"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-base"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-12"
      >
        <div
          v-if="showContent"
          class="z-30 flex size-full items-center"
          @keydown="handleKeyDown"
        >
          <slot name="content" />
        </div>
      </Transition>

      <Transition
        enter-active-class="transition-base"
        enter-from-class="opacity-0 translate-y-12"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-base"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-12"
        mode="out-in"
      >
        <div
          v-if="!showContent"
          ref="inputContainerRef"
          class="flex size-full items-center transition-all"
        >
          <Transition
            appear
            enter-active-class="transition-base"
            enter-from-class="opacity-0 translate-y-12"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-base"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-12"
            mode="out-in"
          >
            <div
              v-if="$slots.prefix && selectedCompletion"
              class="mr-2"
            >
              <slot
                :completion="selectedCompletion"
                name="prefix"
              />
            </div>
          </Transition>

          <slot name="input">
            <input
              ref="inputRef"
              v-model="model"
              class="transition-base w-full border-none bg-transparent p-0 text-black focus:outline-none focus:ring-0 sm:text-base dark:text-white"
              :class="{ 'text-transparent opacity-0 dark:text-transparent': animating }"
              :disabled="animating || disabled"
              :style="inputStyle"
              type="text"
              @blur="handleBlur"
              @focus="handleFocus"
              @keydown="handleKeyDown"
            >
          </slot>
        </div>
      </Transition>
    </div>

    <Transition
      v-if="completable"
      enter-active-class="spring-bounce-10 spring-duration-75"
      enter-from-class="opacity-0 translate-y-8"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="spring-bounce-10 spring-duration-75"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-8"
    >
      <div
        v-if="completions.length > 0 && !loading && !(completionsForce === false)"
        class="absolute left-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 cursor-pointer overflow-hidden rounded-xl bg-white p-2 shadow-lg dark:bg-gray-950"
        :class="[
          completionsDirection === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2',
        ]"
      >
        <div
          v-for="(completion, index) in displayedCompletions"
          :key="completion.key"
          class="w-full rounded-xl p-2 text-left"
          :class="{
            'bg-gray-100 dark:bg-gray-900': completionsDirection === 'top'
              ? index === (completions.length - 1 - selectedIndex)
              : index === selectedIndex,
          }"
          @mousemove="isKeyboardNavigation = false"
          @mouseover="() => {
            if (!isKeyboardNavigation) {
              selectedIndex = completionsDirection === 'top'
                ? completions.length - 1 - index
                : index
            }
          }"
        >
          <slot
            :completion="completion"
            name="completion-item"
            :select-completion="selectCompletion"
          />
        </div>
      </div>
    </Transition>

    <UTooltip
      v-if="showCleanup"
      class="transition-base absolute right-2 top-1/2 z-30 size-8 -translate-y-1/2"
      :class="{
        'scale-75 opacity-0': forceButton || (!showCleanup || (!forceCleanup && (!selectedCompletion || !model))),
        'right-10 scale-100 opacity-100': !forceButton && showCleanup && (forceCleanup || (selectedCompletion || model)),
      }"
      :popper="{ placement: 'bottom' }"
    >
      <template #text>
        <span class="flex items-center gap-2">
          {{ cleanupTooltip }}

          <UKbd
            size="xs"
            value="Escape"
          />
        </span>
      </template>

      <div
        ref="cleanupButtonRef"
        class="transition-base flex size-8 cursor-pointer items-center justify-center rounded-full bg-transparent"
        @click="handleCleanup"
      >
        <UIcon :name="getIcon('trash')" />
      </div>
    </UTooltip>

    <div
      v-if="$slots.button"
      class="transition-base absolute top-1/2 z-30 size-8 -translate-y-1/2"
      :class="{
        'right-2 scale-75 opacity-0': !forceButton && (forceCleanup || selectedCompletion || model),
        'right-10 scale-100 opacity-100': forceButton || (!forceCleanup && !selectedCompletion && !model),
      }"
    >
      <slot name="button" />
    </div>

    <UTooltip
      class="absolute right-2 top-1/2 z-30 size-8 -translate-y-1/2"
      :popper="{ placement: 'bottom' }"
    >
      <template #text>
        <span class="flex items-center gap-2">
          {{ submitTooltip }}

          <UKbd
            size="xs"
            value="Enter"
          />
        </span>
      </template>

      <button
        ref="submitButtonRef"
        class="transition-base flex size-8 items-center justify-center rounded-full bg-gray-900 dark:bg-gray-900"
        :class="{
          'disabled:bg-white dark:disabled:bg-gray-950': !loading,
          '!bg-opacity-0 disabled:bg-gray-100/[0] dark:bg-gray-900/[0] dark:disabled:bg-gray-800/[0]': loading,
          'bg-green-300 text-white hover:bg-green-400 active:bg-green-400 dark:bg-green-700 dark:hover:bg-green-800 dark:active:bg-green-800': canSubmit,
        }"
        :disabled="!model && !forceSubmit && !canSubmit"
        tabindex="1"
        type="submit"
      >
        <svg
          class="transition-base absolute size-4 text-gray-300"
          :class="{
            'opacity-0': loading || canSubmit,
            'opacity-100': !loading,
          }"
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0h24v24H0z"
            fill="none"
            stroke="none"
          />

          <path
            class="spring-duration-100 spring-bounce-10"
            d="M5 12l14 0"
            :style="{
              strokeDasharray: '50%',
              strokeDashoffset: model || (canSubmit || forceSubmit) ? '0' : '50%',
            }"
          />

          <path d="M13 18l6 -6" />

          <path d="M13 6l6 6" />
        </svg>

        <UIcon
          v-if="canSubmit"
          class="absolute size-4"
          :class="{
            'opacity-0': loading || !canSubmit,
            'opacity-100': !loading && (canSubmit || forceSubmit),
          }"
          :name="getIcon('play')"
        />

        <BaseSpinner
          v-if="loading"
          class="absolute size-6"
          color="indigo"
        />
      </button>
    </UTooltip>

    <div class="pointer-events-none absolute inset-0 z-20 flex h-full select-none items-center overflow-hidden px-6">
      <Transition
        enter-active-class="transition-base"
        enter-from-class="opacity-0 translate-y-12"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-base"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-12"
        mode="out-in"
      >
        <div
          v-if="!model && !completions.length && !showContent && !$slots.input"
          class="pointer-events-none z-20 flex w-full items-center rounded-full"
        >
          <div class="flex size-full items-center transition-all">
            <Transition
              enter-active-class="transition-base"
              enter-from-class="opacity-0 translate-y-12"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition-base"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-12"
              mode="out-in"
            >
              <div
                v-if="$slots.prefix && selectedCompletion"
                class="mr-2 opacity-0"
              >
                <slot
                  :completion="selectedCompletion"
                  name="prefix"
                />
              </div>
            </Transition>

            <div class="size-full pr-12">
              <AnimationRotating
                class="overflow-hidden"
                direction="vertical"
                :interval="4000"
                :slots="placeholders.map((_, index) => `placeholder-${index}`)"
              >
                <template
                  v-for="(placeholder, index) in placeholders"
                  :key="`placeholder-${index}`"
                  #[`placeholder-${index}`]
                >
                  <p class="w-full truncate text-left font-normal text-neutral-500 sm:text-base dark:text-gray-500">
                    {{ placeholder || t('placeholder') }}
                  </p>
                </template>
              </AnimationRotating>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </form>
</template>

<i18n lang="json">
{
  "en": {
    "placeholder": "Search or ask a question..."
  },
  "es": {
    "placeholder": "Busca o pregunta cualquier cosa..."
  },
  "fr": {
    "placeholder": "Recherchez ou posez une question..."
  }
}
</i18n>
